import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Download, ExternalLink, Loader2, Lock, LogOut, Pickaxe, RefreshCw, Save, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Reveal, { Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bwpay_admin_token";

const inputCls =
  "w-full rounded-lg border border-line bg-panel px-4 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-sgreen/60";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mut";
const short = (w) => `${w.slice(0, 6)}…${w.slice(-4)}`;

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("admin@bnwpay.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [pool, setPool] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [referralPool, setReferralPool] = useState("");
  const [presale, setPresale] = useState(null);
  const [mining, setMining] = useState(null);
  const [miningTx, setMiningTx] = useState({});

  const load = useCallback(async (t) => {
    try {
      const r = await fetch(`${API}/admin/airdrop`, { headers: { Authorization: `Bearer ${t}` } });
      if (r.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        return;
      }
      const d = await r.json();
      setData(d);
      setPool(d.settings.pool);
      setEndsAt(d.settings.ends_at?.slice(0, 16) || "");
      setReferralPool(d.settings.referral_pool || "100,000");
      try {
        const p = await fetch(`${API}/admin/presale`, { headers: { Authorization: `Bearer ${t}` } });
        if (p.ok) setPresale(await p.json());
      } catch (e) {}
      try {
        const m = await fetch(`${API}/admin/mining`, { headers: { Authorization: `Bearer ${t}` } });
        if (m.ok) setMining(await m.json());
      } catch (e) {}
    } catch (e) {
      toast.error("Could not load admin data");
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Login failed");
      localStorage.setItem(TOKEN_KEY, d.token);
      setToken(d.token);
      setPassword("");
      toast.success("Admin session started");
    } catch (e) {
      toast.error(e?.message || "Login failed");
    }
    setBusy(false);
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/airdrop/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pool, ends_at: endsAt ? new Date(endsAt).toISOString() : "", referral_pool: referralPool }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      setData((prev) => ({ ...prev, settings: d }));
      toast.success("Airdrop settings updated", { description: "Live on the airdrop page immediately" });
    } catch (e) {
      toast.error(e?.message || "Save failed");
    }
    setBusy(false);
  };

  const copyText = (text, successMsg) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(successMsg);
      } catch (e) {
        toast.error("Copy failed — select and copy manually");
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(successMsg), fallback);
    } else {
      fallback();
    }
  };

  const copy = (text) => copyText(text, "Address copied");

  const TASK_COUNT = 5;
  const filtered = (data?.participants || []).filter((p) => {
    if (filter === "full" && p.task_count < TASK_COUNT) return false;
    if (filter === "partial" && (p.task_count === 0 || p.task_count >= TASK_COUNT)) return false;
    if (filter === "claimed" && !p.claimed) return false;
    if (query && !p.wallet.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export in this view");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["wallet", "tasks_verified", "task_ids", "points", "claimed", "joined_at", "proofs", "referrals", "referred_by"].map(esc).join(","),
      ...filtered.map((p) =>
        [
          p.wallet,
          p.task_count,
          (p.tasks || []).join(" "),
          p.points,
          p.claimed ? "yes" : "no",
          p.joined_at || "",
          Object.entries(p.proofs || {}).map(([k, v]) => `${k}: ${v}`).join(" | "),
          p.referral_count ?? 0,
          p.referred_by || "",
        ].map(esc).join(",")
      ),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bwpay-airdrop-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} wallet${filtered.length === 1 ? "" : "s"} to CSV`, {
      description: filter === "full" ? "Allowlist: fully completed only" : `View: ${filter}`,
    });
  };

  const copyAllowlist = () => {
    const full = (data?.participants || []).filter((p) => p.task_count >= TASK_COUNT).map((p) => p.wallet);
    if (!full.length) {
      toast.error("No wallet has completed all quests yet");
      return;
    }
    copyText(full.join("\n"), `Allowlist copied — ${full.length} wallet${full.length === 1 ? "" : "s"}`);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  };

  const markWithdrawalPaid = async (wallet) => {
    const tx = (miningTx[wallet] || "").trim();
    if (tx.length < 8) {
      toast.error("Paste the USDC payout transaction signature first");
      return;
    }
    try {
      const r = await fetch(`${API}/admin/mining/withdrawal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wallet, tx }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not mark as paid");
      toast.success(`Marked paid — ${d.paid} USDC to ${short(wallet)}`);
      setMiningTx((prev) => ({ ...prev, [wallet]: "" }));
      load(token);
    } catch (e) {
      toast.error(e?.message || "Could not mark as paid");
    }
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 pt-[72px]" data-testid="admin-login-page">
        <div className="w-full max-w-sm rounded-2xl border border-line bg-card2 p-8">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-5 w-5 text-sgreen" />
            <h1 className="text-lg font-semibold text-white">B&W Pay Admin</h1>
          </div>
          <p className="mt-2 text-sm text-mut">Airdrop management console.</p>
          <div className="mt-6">
            <label className={labelCls} htmlFor="admin-email-input">Admin email</label>
            <input id="admin-email-input" data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div className="mt-4">
            <label className={labelCls} htmlFor="admin-password-input">Password</label>
            <input
              id="admin-password-input"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              className={inputCls}
              placeholder="••••••••••••"
            />
          </div>
          <button
            onClick={login}
            disabled={busy || !password}
            data-testid="admin-login-button"
            className="cta-glow mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-3.5 text-sm font-semibold text-black disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Sign in
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen px-4 pb-24 pt-[120px] sm:px-6 lg:px-12" data-testid="admin-panel">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Eyebrow>B&W Pay Admin</Eyebrow>
              <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Airdrop console</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => load(token)}
                data-testid="admin-refresh-button"
                className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs text-white transition-colors hover:border-white/30"
              >
                <RefreshCw className="h-3.5 w-3.5 text-sgreen" />
                Refresh
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs text-mut transition-colors hover:border-white/30 hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </button>
            </div>
          </div>
        </Reveal>

        {data && (
          <Reveal delay={0.08}>
            <div className="mt-8 grid grid-cols-3 gap-4" data-testid="admin-stats">
              {[
                ["Participants", data.participants_count],
                ["Fully completed", data.fully_completed],
                ["BWXP awarded", data.total_points.toLocaleString()],
              ].map(([k, v]) => (
                <div key={k} className="rounded-2xl border border-line bg-card2 p-5" data-testid={`admin-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="font-mono text-2xl font-semibold text-white">{v}</p>
                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{k}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {data && (
          <Reveal delay={0.12}>
            <div className="mt-6 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="admin-settings-card">
              <h2 className="text-lg font-semibold text-white">Airdrop settings</h2>
              <p className="mt-1.5 text-sm text-mut">Changes apply to the public airdrop page immediately.</p>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="admin-pool-input">Reward pool (display text)</label>
                  <input id="admin-pool-input" data-testid="admin-pool-input" type="text" value={pool} onChange={(e) => setPool(e.target.value)} className={inputCls} placeholder="1,000,000" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="admin-ends-input">Countdown end (UTC)</label>
                  <input id="admin-ends-input" data-testid="admin-ends-input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="admin-refpool-input">Referral bonus pool (display text)</label>
                  <input id="admin-refpool-input" data-testid="admin-refpool-input" type="text" value={referralPool} onChange={(e) => setReferralPool(e.target.value)} className={inputCls} placeholder="100,000" />
                </div>
              </div>
              <button
                onClick={saveSettings}
                disabled={busy}
                data-testid="admin-save-button"
                className="cta-glow mt-6 inline-flex items-center gap-2 rounded-full bg-sgreen px-6 py-3 text-sm font-semibold text-black disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save settings
              </button>
            </div>
          </Reveal>
        )}

        {presale && (
          <Reveal delay={0.14}>
            <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-card2" data-testid="admin-presale-card">
              <div className="border-b border-line px-6 py-5">
                <h2 className="text-lg font-semibold text-white">
                  Presale orders ({presale.totals.count})
                </h2>
                <p className="mt-1 text-sm text-mut">
                  Raised ${presale.totals.usd_raised.toLocaleString()} · {presale.totals.tokens_sold.toLocaleString()} B&W owed to buyers. Fund the distribution wallet (7sZr…fUYV) with B&W + SOL and deliveries fire automatically.
                </p>
              </div>
              {presale.purchases.length === 0 ? (
                <p className="px-6 py-10 text-center font-mono text-xs text-mut" data-testid="admin-presale-empty">No presale purchases yet.</p>
              ) : (
                <div className="divide-y divide-[#1a1a1a]">
                  {presale.purchases.map((o, i) => (
                    <div key={o.id} className="px-4 py-5 sm:px-6" data-testid={`admin-order-row-${i}`}>
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                        <span className="flex min-w-0 items-center gap-2.5 font-mono text-sm text-white" data-testid={`admin-order-wallet-${i}`}>
                          <span className="min-w-0 truncate" title={o.wallet}>{o.wallet}</span>
                          <button onClick={() => copy(o.wallet)} data-testid={`admin-order-copy-${i}`} aria-label="Copy buyer address" className="shrink-0 text-mut transition-colors hover:text-sgreen">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </span>
                        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 font-mono text-[11px]">
                          <span className="whitespace-nowrap text-white">{o.amount} {o.currency}</span>
                          <span className="whitespace-nowrap text-sgreen">+{o.tokens.toLocaleString()} B&W</span>
                          <span
                            data-testid={`admin-order-status-${i}`}
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 ${
                              o.status === "confirmed"
                                ? "bg-sgreen/10 text-sgreen"
                                : o.status === "failed"
                                  ? "bg-red-400/10 text-red-400"
                                  : "bg-white/[0.06] text-mut"
                            }`}
                          >
                            {o.status}
                          </span>
                          <span
                            data-testid={`admin-order-delivery-${i}`}
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 ${
                              o.delivery_status === "sent"
                                ? "bg-sgreen/10 text-sgreen"
                                : o.delivery_status === "failed"
                                  ? "bg-red-400/10 text-red-400"
                                  : "bg-white/[0.06] text-mut"
                            }`}
                          >
                            {o.delivery_status === "sent" ? "delivered" : o.delivery_status === "failed" ? "retry limit" : "delivering…"}
                          </span>
                          <span className="whitespace-nowrap text-mut">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</span>
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-4 font-mono text-[11px]">
                        {o.signature && (
                          <a href={`https://solscan.io/tx/${o.signature}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-mut transition-colors hover:text-sgreen">
                            payment tx <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {o.delivery_signature && (
                          <a href={`https://solscan.io/tx/${o.delivery_signature}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-order-delivery-tx-${i}`} className="inline-flex items-center gap-1 text-sgreen">
                            delivery tx <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {o.fail_reason && <span className="text-red-400">{o.fail_reason}</span>}
                        {o.delivery_status !== "sent" && o.delivery_note && <span className="text-mut">{o.delivery_note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}

        {mining && (
          <Reveal delay={0.15}>
            <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-card2" data-testid="admin-mining-card">
              <div className="border-b border-line px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
                      <Pickaxe className="h-4 w-4 text-sgreen" />
                      USDC Mining
                    </h2>
                    <p className="mt-1 text-sm text-mut">
                      {mining.totals.miners.toLocaleString()} miners total · {mining.totals.accrued_usdc.toLocaleString()} USDC mined — approve USDC payouts below.
                    </p>
                  </div>
                  <span className="rounded-full bg-sgreen/10 px-3 py-1.5 font-mono text-[11px] text-sgreen" data-testid="admin-mining-pending-count">
                    {mining.totals.pending_withdrawals} pending withdrawal{mining.totals.pending_withdrawals === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3" data-testid="admin-mining-tier-breakdown">
                  {[
                    ["Free", "free", "mining-tier-free"],
                    ["Tier 1", "tier1", "mining-tier-tier1"],
                    ["Tier 2", "tier2", "mining-tier-tier2"],
                  ].map(([label, key, tid]) => (
                    <div key={key} className="rounded-xl border border-line bg-panel p-3.5 text-center" data-testid={tid}>
                      <p className="font-mono text-lg font-semibold text-white">
                        {mining.totals.tiers[key].taken.toLocaleString()}
                        <span className="text-xs text-mut"> / {mining.totals.tiers[key].cap.toLocaleString()}</span>
                      </p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{label} miners</p>
                    </div>
                  ))}
                </div>
              </div>
              {mining.withdrawals.length > 0 && (
                <div className="border-b border-line px-4 py-5 sm:px-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mut">Withdrawal requests</p>
                  <div className="mt-3 space-y-3">
                    {mining.withdrawals.map((w, i) => (
                      <div key={w.wallet} className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4 sm:flex-row sm:items-center" data-testid={`admin-mining-withdrawal-${i}`}>
                        <div className="min-w-0 flex-1">
                          <p className="flex min-w-0 items-center gap-2 font-mono text-sm text-white">
                            <span className="min-w-0 truncate" title={w.wallet}>{w.wallet}</span>
                            <button onClick={() => copy(w.wallet)} data-testid={`admin-mining-copy-${i}`} aria-label="Copy miner address" className="shrink-0 text-mut transition-colors hover:text-sgreen">
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-mut">
                            {w.tier_name} · mined {w.accrued} USDC · day {w.day}/{w.mining_days} · requested {w.withdrawal_requested_at ? new Date(w.withdrawal_requested_at).toLocaleDateString() : "—"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="whitespace-nowrap font-mono text-sm font-semibold text-sgreen">{w.available} USDC</span>
                          <input
                            value={miningTx[w.wallet] || ""}
                            onChange={(e) => setMiningTx((prev) => ({ ...prev, [w.wallet]: e.target.value }))}
                            placeholder="USDC payout tx signature"
                            data-testid={`admin-mining-tx-input-${i}`}
                            className="w-full min-w-0 rounded-lg border border-line bg-card2 px-3 py-2.5 font-mono text-xs text-white outline-none transition-colors focus:border-sgreen/60 sm:w-56"
                          />
                          <button
                            onClick={() => markWithdrawalPaid(w.wallet)}
                            data-testid={`admin-mining-paid-button-${i}`}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sgreen px-4 py-2.5 text-xs font-semibold text-black"
                          >
                            <Check className="h-3.5 w-3.5" /> Paid
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {mining.accounts.length === 0 ? (
                <p className="px-6 py-10 text-center font-mono text-xs text-mut" data-testid="admin-mining-empty">No miners yet.</p>
              ) : (
                <div className="max-h-[420px] divide-y divide-[#1a1a1a] overflow-y-auto">
                  {mining.accounts.map((a, i) => (
                    <div key={a.wallet} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3.5 sm:px-6" data-testid={`admin-mining-row-${i}`}>
                      <span className="flex min-w-0 items-center gap-2 font-mono text-xs text-white">
                        <span className="min-w-0 truncate" title={a.wallet}>{a.wallet}</span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
                        <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-white/70">{a.tier_name}</span>
                        <span className="text-sgreen">{a.accrued} USDC</span>
                        <span className="text-mut">day {a.day}/{a.mining_days}</span>
                        {a.withdrawal_status === "paid" && <span className="rounded-full bg-sgreen/10 px-2 py-0.5 text-sgreen">paid</span>}
                        {a.withdrawal_status === "requested" && <span className="rounded-full bg-sgreen/10 px-2 py-0.5 text-sgreen">withdraw pending</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}

        {data && (
          <Reveal delay={0.16}>
            <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-card2" data-testid="admin-participants-card">
              <div className="border-b border-line px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      Participants ({data.participants.length})
                    </h2>
                    <p className="mt-1 text-sm text-mut">Full wallet addresses, verified quests, and proof links.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={exportCsv}
                      data-testid="admin-export-csv-button"
                      className="inline-flex items-center gap-2 rounded-full bg-sgreen px-4 py-2.5 text-xs font-semibold text-black"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export CSV
                    </button>
                    <button
                      onClick={copyAllowlist}
                      data-testid="admin-copy-allowlist-button"
                      className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-xs text-white transition-colors hover:border-white/30"
                    >
                      <Copy className="h-3.5 w-3.5 text-sgreen" />
                      Copy allowlist
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[220px] flex-1">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mut" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search wallet address…"
                      data-testid="admin-search-input"
                      className="w-full rounded-lg border border-line bg-panel py-2.5 pl-10 pr-4 font-mono text-xs text-white outline-none transition-colors focus:border-sgreen/60"
                    />
                  </div>
                  {[
                    ["all", "All"],
                    ["full", `Fully completed (${data.fully_completed})`],
                    ["partial", "In progress"],
                    ["claimed", "Claimed"],
                  ].map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setFilter(id)}
                      data-testid={`admin-filter-${id}`}
                      className={`rounded-full px-4 py-2.5 font-mono text-[11px] transition-colors ${
                        filter === id ? "bg-sgreen/15 text-sgreen" : "border border-line text-mut hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {filtered.length === 0 ? (
                <p className="px-6 py-10 text-center font-mono text-xs text-mut" data-testid="admin-participants-empty">
                  {data.participants.length === 0 ? "No participants yet." : "No participants match this view."}
                </p>
              ) : (
                <div className="divide-y divide-[#1a1a1a]">
                  {filtered.map((p, i) => (
                    <div key={p.wallet} className="px-4 py-5 sm:px-6" data-testid={`admin-wallet-row-${i}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-2.5 font-mono text-sm text-white" data-testid={`admin-wallet-address-${i}`}>
                          <span className="min-w-0 truncate" title={p.wallet}>{p.wallet}</span>
                          <button onClick={() => copy(p.wallet)} data-testid={`admin-copy-${i}`} aria-label="Copy address" className="shrink-0 text-mut transition-colors hover:text-sgreen">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </span>
                        <span className="flex items-center gap-2 font-mono text-[11px]">
                          <span className="text-sgreen">{p.points} BWXP</span>
                          {!!p.referral_count && <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-white/70">+{p.referral_count} ref</span>}
                          {p.claimed && <span className="rounded-full bg-sgreen/10 px-2 py-0.5 text-sgreen">claimed</span>}
                          <span className="text-mut">{p.joined_at ? new Date(p.joined_at).toLocaleDateString() : "—"}</span>
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {p.tasks.length === 0 ? (
                          <span className="font-mono text-[11px] text-mut">No quests verified yet</span>
                        ) : (
                          p.tasks.map((t) => (
                            <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-sgreen/25 bg-sgreen/10 px-2.5 py-0.5 font-mono text-[10px] text-sgreen">
                              <Check className="h-3 w-3" />
                              {t.replace(/-/g, " ")}
                              {p.proofs?.[t] && (
                                <a href={p.proofs[t]} target="_blank" rel="noopener noreferrer" data-testid={`admin-proof-${i}-${t}`} className="inline-flex items-center gap-1 text-white/60 underline decoration-dotted underline-offset-2 hover:text-sgreen">
                                  proof
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
